import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Switch } from '@mui/material';
import AppDownloadLinks from 'components/AppDownloadLinks/AppDownloadLinks';
import Header from 'components/Header/Header';
import HeaderIconLogo from 'components/Header/header-items/HeaderIconLogo';
import BlockTitle from 'components/layout/BlockTitle/BlockTitle';
import Button from 'components/ui-kit/Button/Button';
import Modal from 'components/ui-kit/Modal/Modal';
import withAuth from 'components/with-auth/with-auth';
import { MY } from 'config/fe-routes';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import UserService from 'services/user.service';
import { UserType } from 'types/user.types';
import { useNotification } from 'utils/snackbar';

const userService = new UserService();

type NotificationPreference = {
  key: string;
  channel: string;
  value: boolean;
};

type Props = {
  user: UserType;
};

const mapStateToProps = ({ userData }) => ({
  user: userData && userData.user,
});

const MyProfileNotificationsPage = (props: Props) => {
  const { user } = props;
  const { t } = useTranslation();
  const router = useRouter();

  const [consentMarketingCommunications, setConsentMarketingCommunications] = useState(user?.consentMarketingCommunications ?? false);
  const [emailPreferences, setEmailPreferences] = useState<NotificationPreference[]>([]);
  const [marketingModalOpen, setMarketingModalOpen] = useState(false);

  const { showErrorNotification, showSuccessNotification } = useNotification();

  useEffect(() => {
    (async () => {
      const [userData, preferences] = await Promise.all([userService.getCurrentUser(), userService.getNotificationPreferences()]);

      setConsentMarketingCommunications(userData?.consentMarketingCommunications ?? false);
      setEmailPreferences((preferences ?? []).filter((p: NotificationPreference) => p.channel === 'email'));
    })();
  }, []);

  const handleToggle = async (key: string, currentValue: boolean) => {
    if (key === 'marketing_comms' && !consentMarketingCommunications && !currentValue) {
      setMarketingModalOpen(true);

      return;
    }

    const newValue = !currentValue;

    setEmailPreferences((prev) => prev.map((p) => (p.key === key ? { ...p, value: newValue } : p)));

    try {
      await userService.updateNotificationPreference({ key, channel: 'email', value: newValue, source: 'settings' });
      showSuccessNotification(t('pages.settings.successfully-saved'));
    } catch (error) {
      setEmailPreferences((prev) => prev.map((p) => (p.key === key ? { ...p, value: currentValue } : p)));

      if (error?.response?.data?.errorCode === 'MARKETING_CONSENT_REQUIRED') {
        setMarketingModalOpen(true);
      } else {
        showErrorNotification({ message: t('pages.settings.saving-error'), error });
      }
    }
  };

  const handleMarketingModalCta = () => {
    setMarketingModalOpen(false);
    router.push(MY.SETTINGS_PRIVACY().as);
  };

  return (
    <>
      <Header>
        <HeaderIconLogo icon="back" showLogo={false} />
      </Header>

      <div className="settings-detail-page settings-notification-page mobile-full-width">
        <BlockTitle mt={0}>{t('pages.settings.profile-notifications.title')}</BlockTitle>

        {/* EMAIL NOTIFICATIONS */}
        <div className="privacy-section">
          <div className="privacy-section-header">
            <img src="/icons/icn-pref-email.svg" alt="" className="privacy-section-icon" />
            <span className="privacy-section-title">{t('pages.settings.profile-notifications.email-notifications')}</span>
          </div>

          {emailPreferences.map(({ key, value }) => (
            <div key={`email-${key}`} className="privacy-toggle-row">
              <div className="notification-preference-info">
                <div className="privacy-toggle-label">
                  {t(`pages.settings.profile-notifications.email.${key}.label` as any)}
                  {key === 'marketing_comms' && !consentMarketingCommunications && (
                    <InfoOutlinedIcon
                      className="notification-info-icon"
                      fontSize="small"
                      onClick={() => setMarketingModalOpen(true)}
                      titleAccess={t('pages.settings.profile-notifications.marketing-consent-required')}
                    />
                  )}
                </div>
                <div className="privacy-description">{t(`pages.settings.profile-notifications.email.${key}.description` as any)}</div>
              </div>
              <Switch
                edge="end"
                checked={value}
                onChange={() => handleToggle(key, value)}
                color="primary"
                inputProps={{ 'aria-label': t(`pages.settings.profile-notifications.email.${key}.label` as any) }}
              />
            </div>
          ))}
        </div>

        <div className="privacy-divider" />

        {/* PUSH NOTIFICATIONS */}
        <div className="privacy-section">
          <div className="privacy-section-header">
            <img src="/icons/icn-pref-push.svg" alt="" className="privacy-section-icon" />
            <span className="privacy-section-title">{t('pages.settings.profile-notifications.push-notifications')}</span>
          </div>

          <div className="push-note">{t('pages.settings.profile-notifications.push-notifications-note')}</div>
          <AppDownloadLinks maxWidth={300} />
        </div>
      </div>

      <Modal
        open={marketingModalOpen}
        onClose={() => setMarketingModalOpen(false)}
        title={t('pages.settings.profile-notifications.marketing-sheet.title')}
        contentPaddingInline={24}
        actions={
          <Button buttonType="primary" size="large" fullWidth onClick={handleMarketingModalCta}>
            {t('pages.settings.profile-notifications.marketing-sheet.cta')}
          </Button>
        }
      >
        <p
          dangerouslySetInnerHTML={{
            __html: t('pages.settings.profile-notifications.marketing-sheet.body-web' as any, { privacy: MY.SETTINGS_PRIVACY().as }),
          }}
        />
      </Modal>
    </>
  );
};

export default withAuth(connect(mapStateToProps)(MyProfileNotificationsPage));
